import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Typography, Stack, TextField, Button, Card, IconButton, Box, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { getPost, Post } from "../api/posts";
import { createComment, getComments, Comment, deleteComment } from "../api/comments";
import CommentVoteButtons from "../components/CommentVoteButtons";
import { timeAgo } from "../utils/timeAgo";
import { getMyCommentVotes } from "../api/commentVotes";
import { getMyVotes } from "../api/votes";
import { getMyProfile } from "../api/profile";
import PostCard from "../components/PostCard";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";


export default function PostPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const postId = Number(id);

  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [content, setContent] = useState("");
  const [myCommentVotes, setMyCommentVotes] = useState<Record<number, number>>({});
  const [myPostVote, setMyPostVote] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [deleteCommentId, setDeleteCommentId] = useState<number | null>(null);

  async function refresh() {
    const p = await getPost(postId);
    const c = await getComments(postId);

    setPost(p);
    setComments(c || []);

    const token = localStorage.getItem("token");
    if (token) {
        // Comment Votes
        const rawComments = await getMyCommentVotes();
        const mappedComments: Record<number, number> = {};
        for (const [k, v] of Object.entries(rawComments)) mappedComments[Number(k)] = v;
        setMyCommentVotes(mappedComments);

        // Post Vote - Need to fetch singular vote or find it in list
        const rawPostVotes = await getMyVotes();
        setMyPostVote(rawPostVotes[postId] ?? 0);

        getMyProfile().then(me => setCurrentUserId(me.user_id)).catch(() => {});

    } else {
        setMyCommentVotes({});
        setMyPostVote(0);
        setCurrentUserId(null);
    }
  }

  useEffect(() => {
    refresh();
  }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submitComment() {
    if (!localStorage.getItem("token")) {
        navigate("/login");
        return;
    }
    if (!content.trim()) return;

    try {
        await createComment(postId, content);
        setContent("");
        await refresh();
    } catch (e: any) {
        if (e.message.includes("invalid token") || e.message.includes("401")) {
            localStorage.removeItem("token");
            navigate("/login");
        } else {
            console.error("Failed to post comment:", e);
            alert("Failed to post comment. Please try again.");
        }
    }
  }

  async function confirmDeleteComment() {
      if (!deleteCommentId) return;
      try {
          await deleteComment(deleteCommentId);
          await refresh();
      } catch {
          alert("Failed to delete comment");
      } finally {
          setDeleteCommentId(null);
      }
  }

  if (!post) return null;


  return (
    <>
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ 
            mb: 2, 
            px: 3,
            py: 1,
            borderRadius: 3,
            color: 'primary.main',
            bgcolor: (theme) =>  theme.palette.mode === 'light' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(129, 140, 248, 0.1)',
            fontWeight: 700,
            fontSize: '0.95rem',
            textTransform: 'none',
            '&:hover': { 
                bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(129, 140, 248, 0.2)',
                transform: 'translateX(-4px)'
            },
            transition: 'all 0.2s',
            boxShadow: 'none'
          }}
          disableRipple
        >
          Back to Feed
        </Button>

        <PostCard 
            post={post} 
            myVote={myPostVote} 
            onVoted={refresh} 
            currentUserId={currentUserId}
            onDelete={() => navigate('/')}
        />

        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 4 }}>
            Comments ({comments?.length ?? 0})
        </Typography>

        <Stack spacing={2} sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                    placeholder="What are your thoughts?"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    variant="outlined"
                    sx={{ bgcolor: 'background.paper' }}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={submitComment} disabled={!content.trim()}>
                    Post Comment
                </Button>
            </Box>
        
            <Divider sx={{ my: 2 }} />

            {comments.map((c) => (
            <Card key={c.comment_id} variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    {c.username}
                                </Typography>
                                {currentUserId === c.author_id && (
                                    <IconButton size="small" onClick={() => setDeleteCommentId(c.comment_id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, p: 0.5 }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                {timeAgo(c.created_at)}
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {c.content}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CommentVoteButtons
                            commentId={c.comment_id}
                            score={c.vote_score}
                            myVote={myCommentVotes[c.comment_id] ?? 0}
                            onVoted={refresh}
                        />
                        <Button startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />} size="small" color="inherit" sx={{ borderRadius: 4 }}>
                            Reply
                        </Button>
                    </Box>
                </Box>
            </Card>
            ))}
        </Stack>
      </Box>
    </Container>
    <DeleteConfirmDialog 
        open={!!deleteCommentId} 
        onClose={() => setDeleteCommentId(null)} 
        onConfirm={confirmDeleteComment}
        title="Delete Comment?"
        description="Are you sure you want to delete this comment?"
    />
    </>
  );
}
